import { useRef, useState } from 'react';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { useSubscription } from '@meraki-internal/state';
import { CaremapNotesModelFactory } from '../model/CaremapNotesModelFactory';
import { ICaremapNoteResponse } from '../model/NotesClient';
import { NoteUnavailableError } from '../model/NotesModel';
import { useAsyncEffect } from '../../support/useAsyncEffect';

// 'resolving' covers both the initial bulk load and a targeted fetch, because a view has the same
// job in each case: wait, rather than tell the user anything about the note yet. 'unavailable' means
// the server answered that the note is not ours to show; 'error' means we never got an answer, and
// the two must stay separate so a dropped request is not reported as a deleted note.
export type INoteResolution = 'resolving' | 'found' | 'unavailable' | 'error';

interface IAttempt {
    noteId: string;
    // Absent until the fetch settles, so a still-running attempt is not mistaken for a finished one.
    outcome?: 'unavailable' | 'error';
}

export interface IResolvedNote {
    note?: ICaremapNoteResponse;
    resolution: INoteResolution;
    retry: () => void;
}

/**
 * Resolves the note behind a note id, falling back to a targeted fetch when the notes list does not
 * have it. Every view handed only a note id should go through this rather than reading the list
 * directly: the list is loaded in bulk and only converges again on foreground reconcile or a
 * websocket frame, so a note created since that load is absent until one of those happens, and a
 * deep link or push notification arriving in that gap would otherwise render nothing at all.
 */
export const useResolvedNote = (noteId: string): IResolvedNote => {
    const notesFactory = useInstance(CaremapNotesModelFactory);
    useSubscription(() => notesFactory);

    const model = notesFactory.forActiveCaremap();
    const note = model.getNote(noteId);

    // A note that was in the list and then left it was deleted, or made private, out from under the
    // reader. That is already the answer, so it must be reported at once rather than re-checked with
    // the server: the list can transiently resurrect a just-deleted note (a foreground reconcile's
    // list query can still return it for a moment after the delete), and a view that waited for
    // confirmation would see it reappear and go on showing a note that is gone.
    //
    // Holds the id it was set for, not a bare flag: the drawer and the care-map route both reuse one
    // component instance across note ids, so a bare flag would carry the previous note's answer over
    // and report a perfectly good note as deleted.
    const everHadNoteFor = useRef<string | undefined>(undefined);
    if (note) {
        everHadNoteFor.current = noteId;
    }
    const everHadNote = everHadNoteFor.current === noteId;

    // Carries the note id so navigating to a different note starts a fresh attempt rather than
    // inheriting the previous note's outcome.
    const [attempt, setAttempt] = useState<IAttempt | undefined>();

    useAsyncEffect(async () => {
        if (note || !model.hasLoaded || attempt?.noteId === noteId || everHadNote) {
            return;
        }
        // Recorded before awaiting: the model re-renders subscribers when it merges the note, and
        // without this the effect would re-enter and issue a duplicate request.
        setAttempt({ noteId });
        try {
            await model.fetchNoteById(noteId);
        } catch (err: any) {
            setAttempt({ noteId, outcome: err instanceof NoteUnavailableError ? 'unavailable' : 'error' });
        }
    }, [noteId, note, model.hasLoaded, attempt]);

    // Clearing the attempt re-runs the effect, and nothing remembers the previous outcome, so a note
    // that has since become visible resolves normally rather than staying stuck on its old answer.
    const retry = () => setAttempt(undefined);

    if (note) {
        return { note, resolution: 'found', retry };
    }

    if (everHadNote) {
        return { resolution: 'unavailable', retry };
    }

    if (attempt?.noteId === noteId && attempt.outcome) {
        return { resolution: attempt.outcome, retry };
    }

    return { resolution: 'resolving', retry };
};
